import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Package,
  MapPin,
  Clock,
  Truck,
  CheckCircle2,
  AlertCircle,
  Phone,
  ExternalLink,
  type LucideIcon,
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Spinner,
} from "@heroui/react";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDescription,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
  TimelineTitle,
} from "@/components/ui/timeline";
import { BackgroundBeams } from "@/components/ui/background-beams";
import { cn } from "@/lib/utils";
import { useTracking } from "@/api/use-tracking";
import { useState } from "react";

// Types
interface StatusConfig {
  color: string;
  icon: LucideIcon;
  bgColor: string;
  textColor: string;
}

// Constants
const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

const CONTACT_INFO = {
  phone: "647-931-7960",
  phoneLabel: "647-931-7960 (English, Chinese)",
  email: "ezexpress.canada@gmail.com",
  hours: "Monday - Sunday: 12AM - 10PM EST",
} as const;

// Helper Functions
const getStatusConfig = (status: string): StatusConfig => {
  const configs: Record<string, StatusConfig> = {
    DELIVERED: {
      color: "text-green-600",
      bgColor: "bg-green-50",
      icon: CheckCircle2,
      textColor: "text-green-700",
    },
    OUT_FOR_DELIVERY: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      icon: Truck,
      textColor: "text-gray-900",
    },
    IN_TRANSIT: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      icon: Truck,
      textColor: "text-gray-900",
    },
    PICKED_UP: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      icon: CheckCircle2,
      textColor: "text-gray-900",
    },
    LABEL_READY: {
      color: "text-gray-400",
      bgColor: "bg-gray-50",
      icon: Package,
      textColor: "text-gray-900",
    },
    CREATED: {
      color: "text-gray-400",
      bgColor: "bg-gray-50",
      icon: Package,
      textColor: "text-gray-900",
    },
    PENDING_PAYMENT: {
      color: "text-yellow-600",
      bgColor: "bg-yellow-50",
      icon: Clock,
      textColor: "text-yellow-700",
    },
    CANCELLED: {
      color: "text-red-600",
      bgColor: "bg-red-50",
      icon: AlertCircle,
      textColor: "text-red-700",
    },
    default: {
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      icon: Package,
      textColor: "text-gray-900",
    },
  };

  return configs[status] ?? configs.default;
};

const FAQ_KEYS = [
  "cantFindPackage",
  "whereTrackingNumber",
  "changeDeliveryDate",
  "whenReceive",
  "cantReceiveInPerson",
] as const;

export default function TrackingScreen() {
  const { trackingNumber } = useParams<{ trackingNumber: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("tracking");
  const [trackInput, setTrackInput] = useState("");
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const locale = i18n.language === "zh" ? "zh-CN" : "en-US";

  // Date formatting with locale
  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString(locale, {
      ...DATE_FORMAT_OPTIONS,
      timeZone,
    });
  };

  const { data: tracking, isLoading, error } = useTracking(trackingNumber);
  const milestone = tracking?.estimatedMilestone;
  const completion = milestone?.isComplete ? milestone.completedAt : null;
  // Operational windows are wall clocks, not instants. Keep their source zone.
  const estimatedAt = milestone?.timeWindow ? null : milestone?.estimatedAt;
  const estimatedDate = milestone?.timeWindow ? null : milestone?.estimatedDate;
  let estimate = milestone?.estimate?.trim() || null;
  let estimateTimeZone = milestone?.timeZone;
  if (completion) {
    estimate = t("completedAt", { time: formatTimestamp(completion) });
    estimateTimeZone = timeZone;
  } else if (estimatedAt) {
    const day = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const arrival = new Date(estimatedAt);
    estimate =
      day.format(arrival) === day.format(new Date())
        ? t("todayBy", {
            time: arrival.toLocaleTimeString(locale, {
              timeZone,
              hour: "numeric",
              minute: "2-digit",
            }),
          })
        : formatTimestamp(estimatedAt);
    estimateTimeZone = timeZone;
  } else if (estimatedDate) {
    // UTC is only an anchor to render this calendar date without shifting it.
    estimate = new Date(`${estimatedDate}T12:00:00Z`).toLocaleDateString(
      locale,
      {
        timeZone: "UTC",
        month: "short",
        day: "numeric",
        year: "numeric",
      }
    );
    estimateTimeZone = undefined;
  }

  const handleTrack = () => {
    if (trackInput.trim()) {
      void navigate(`/tracking/${trackInput.trim()}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-80px)] items-center justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (error || !tracking) {
    return (
      <div className="flex h-[calc(100vh-80px)] items-center justify-center">
        <Card className="w-full max-w-md p-8 text-center">
          <Package className="mx-auto h-12 w-12 text-default-500" />
          <p className="mt-4 text-lg font-medium text-default-500">
            {t("noTrackingInfo")}
          </p>
          <p className="mt-2 text-sm text-default-500">
            {t("trackingNumber")}: {trackingNumber}
          </p>
        </Card>
      </div>
    );
  }

  const currentStatus = tracking.currentStatus || tracking.status;
  const statusConfig = getStatusConfig(currentStatus);
  const trackingEvents = [...tracking.trackingEvents].sort(
    (a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp)
  );
  const latestEvent = trackingEvents[0];

  return (
    <div className="flex flex-col">
      <BackgroundBeams />
      <div className="container mx-auto max-w-6xl relative">
        <div className="gap-6 grid grid-cols-1 lg:grid-cols-3 py-4 md:py-8 px-4 md:px-0">
          <div className="lg:col-span-2 space-y-4 md:space-y-8">
            {/* Overview Card */}
            <Card className="overflow-hidden">
              <CardHeader className="border-b bg-muted/5 pb-4">
                <div className="grid w-full min-w-0 grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="min-w-0 space-y-2">
                    <div className="text-xs font-medium text-default-500">
                      {t("currentStatus")}
                    </div>
                    <div
                      className={cn(
                        "inline-flex max-w-full items-center gap-2 rounded-full px-4 py-2",
                        statusConfig.bgColor
                      )}
                    >
                      <statusConfig.icon
                        className={cn(
                          "h-4 w-4 shrink-0",
                          statusConfig.textColor
                        )}
                      />
                      <span
                        className={cn(
                          "break-words text-sm font-semibold",
                          statusConfig.textColor
                        )}
                      >
                        {t(`status.${currentStatus}`, {
                          defaultValue: t("status.UNKNOWN"),
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="min-w-0 space-y-2 sm:text-right">
                    <div className="text-xs font-medium text-default-500">
                      {milestone?.type === "CARRIER_DROPOFF"
                        ? t("estimatedCarrierDropoff")
                        : t("estimatedDelivery")}
                    </div>
                    <div className="break-words text-xl font-bold tracking-tight sm:text-2xl">
                      {estimate || t("estimateUnavailable")}
                    </div>
                    {estimate &&
                      (completion || estimatedAt || !estimatedDate) && (
                        <p className="break-words text-sm text-default-500">
                          {estimateTimeZone?.trim()
                            ? t("estimateTimeZone", {
                                timeZone: estimateTimeZone,
                              })
                            : t("estimateTimeZoneUnavailable")}
                        </p>
                      )}
                  </div>
                </div>
              </CardHeader>
              <CardBody className="pt-6">
                {/* Tracking number remains secondary to status and estimate. */}
                <div className="mb-6">
                  <div className="text-xs font-medium uppercase tracking-wider text-default-500 mb-1">
                    {t("trackingNumber")}
                  </div>
                  <div className="break-all text-base font-medium">
                    {trackingNumber}
                  </div>
                </div>
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-default-500">
                      {t("latestUpdate")}
                    </div>
                    <div className="text-base font-medium">
                      {formatTimestamp(
                        latestEvent?.timestamp ?? tracking.lastUpdated
                      )}
                    </div>
                  </div>
                  {latestEvent?.location && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-default-500">
                        {t("currentLocation")}
                      </div>
                      <div className="flex items-center gap-2 text-base">
                        <MapPin className="h-4 w-4 text-default-500 shrink-0" />
                        <span className="font-medium">
                          {latestEvent.location}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <p className="mt-3 break-words text-sm text-default-500">
                  {t("eventTimeZone", { timeZone })}
                </p>
              </CardBody>
            </Card>

            {/* Timeline Card */}
            <Card role="region" aria-label={t("trackingHistory")}>
              <CardHeader className="border-b bg-muted/5">
                <div className="text-lg">{t("trackingHistory")}</div>
              </CardHeader>
              <CardBody className="p-4 sm:p-6">
                {trackingEvents.length === 0 && (
                  <p className="text-default-500">{t("noEvents")}</p>
                )}
                <Timeline>
                  {/* Actual Events */}
                  {trackingEvents.map((event, index) => {
                    const isLast = index === trackingEvents.length - 1;
                    const config = getStatusConfig(event.status);

                    return (
                      <TimelineItem
                        key={index}
                        aria-current={index === 0 ? "step" : undefined}
                      >
                        <TimelineSeparator>
                          <TimelineDot
                            className={cn(config.color, "border-0")}
                          />
                          {!isLast && (
                            <TimelineConnector className="bg-gray-900" />
                          )}
                        </TimelineSeparator>
                        <TimelineContent>
                          <TimelineTitle
                            className={cn("font-medium", config.textColor)}
                          >
                            {t(`status.${event.status}`, {
                              defaultValue: t("status.UNKNOWN"),
                            })}
                          </TimelineTitle>
                          {event.description && (
                            <TimelineDescription className={config.textColor}>
                              {event.description}
                            </TimelineDescription>
                          )}
                          <TimelineDescription className={config.textColor}>
                            {formatTimestamp(event.timestamp)}
                          </TimelineDescription>
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                </Timeline>
              </CardBody>
            </Card>

            {/* FAQ Card */}
            <Card>
              <CardHeader>
                <div className="text-lg">{t("faq.title")}</div>
              </CardHeader>
              <CardBody>
                <Accordion variant="light" selectionMode="single">
                  {FAQ_KEYS.map((faqKey, index) => (
                    <AccordionItem
                      key={`item-${index}`}
                      aria-label={t(`faq.${faqKey}.question`)}
                      title={
                        <span className="text-sm">
                          {t(`faq.${faqKey}.question`)}
                        </span>
                      }
                    >
                      <p className="text-small text-default-500">
                        {t(`faq.${faqKey}.answer`)}
                      </p>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardBody>
            </Card>

            {/* Track Another Package Card */}
            <Card>
              <CardHeader>
                <div className="text-lg">{t("trackAnother")}</div>
              </CardHeader>
              <CardBody>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    placeholder={t("enterTrackingNumber")}
                    value={trackInput}
                    onChange={(e) => setTrackInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleTrack()}
                    className="flex-1"
                  />
                  <Button onClick={handleTrack} className="sm:w-auto">
                    {t("common:track")}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Carrier Tracking Card */}
            {tracking.carrierTracking && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2 text-lg font-semibold">
                    <Truck className="h-5 w-5" />
                    {t("carrierTracking.title")}
                  </div>
                </CardHeader>
                <CardBody className="space-y-4">
                  <div className="space-y-2">
                    <div className="text-xs font-medium uppercase tracking-wider text-default-500">
                      {t("carrierTracking.carrier")}
                    </div>
                    <div className="font-semibold">
                      {tracking.carrierTracking.carrierName ??
                        tracking.carrierTracking.carrier}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-xs font-medium uppercase tracking-wider text-default-500">
                      {t("carrierTracking.trackingNumber")}
                    </div>
                    <div className="font-medium">
                      {tracking.carrierTracking.trackingNumber}
                    </div>
                  </div>
                  {tracking.carrierTracking.carrierTrackingUrl && (
                    <a
                      href={tracking.carrierTracking.carrierTrackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      {t("carrierTracking.trackOnCarrier")}
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Shipping Details Card */}
            <Card className={tracking.carrierTracking ? "mt-4" : ""}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <Package className="h-5 w-5" />
                  {t("shippingDetails.title")}
                </div>
              </CardHeader>
              <CardBody className="space-y-5">
                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-default-500">
                    {t("shippingDetails.from")}
                  </div>
                  <div className="space-y-1">
                    <div className="font-semibold">
                      {tracking.shipmentDetails.senderName ||
                        t("common:sender")}
                    </div>
                    {tracking.shipmentDetails.senderAddress && (
                      <div className="text-sm text-default-500 leading-relaxed">
                        {tracking.shipmentDetails.senderAddress}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-default-500">
                    {t("shippingDetails.to")}
                  </div>
                  <div className="space-y-1">
                    <div className="font-semibold">
                      {tracking.shipmentDetails.recipientName ||
                        t("common:recipient")}
                    </div>
                    {tracking.shipmentDetails.recipientAddress && (
                      <div className="text-sm text-default-500 leading-relaxed">
                        {tracking.shipmentDetails.recipientAddress}
                      </div>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Package Details Card */}
            {tracking.packages && tracking.packages.length > 0 && (
              <Card className="mt-4">
                <CardHeader className="pb-3">
                  <div className="text-lg font-semibold">
                    {t("packageDetails.title")}
                  </div>
                </CardHeader>
                <CardBody className="space-y-4">
                  {tracking.packages.map((pkg, index) => (
                    <div key={index} className="space-y-2">
                      <div className="text-xs font-medium uppercase tracking-wider text-default-500">
                        {t("packageDetails.package")} {pkg.number}
                      </div>
                      <div className="flex items-start gap-3">
                        <Package className="h-4 w-4 text-default-500 shrink-0 mt-1" />
                        <div className="flex-1 space-y-1.5">
                          {pkg.trackingNumber && (
                            <div className="text-sm font-medium break-all">
                              {pkg.trackingNumber}
                            </div>
                          )}
                          {(pkg.weight || pkg.dimensions) && (
                            <div className="text-sm font-medium">
                              {[pkg.weight, pkg.dimensions]
                                .filter(Boolean)
                                .join(" • ")}
                            </div>
                          )}
                          {pkg.description && (
                            <div className="text-sm text-default-500 leading-relaxed">
                              {pkg.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardBody>
              </Card>
            )}

            {/* Contact Us Card */}
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <Phone className="h-5 w-5" />
                  {t("needHelp.title")}
                </div>
              </CardHeader>
              <CardBody className="space-y-4">
                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-default-500">
                    {t("needHelp.customerService")}
                  </div>
                  <a
                    href={`tel:${CONTACT_INFO.phone}`}
                    className="block text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {CONTACT_INFO.phoneLabel}
                  </a>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-default-500">
                    {t("needHelp.emailSupport")}
                  </div>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="block text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline break-all"
                  >
                    {CONTACT_INFO.email}
                  </a>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-default-500">
                    {t("needHelp.hoursOfOperation")}
                  </div>
                  <div className="text-sm font-medium">
                    {CONTACT_INFO.hours}
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Platform Interest Card */}
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <div className="text-lg font-semibold leading-tight">
                  {t("platformInterest.title")}
                </div>
              </CardHeader>
              <CardBody className="space-y-4">
                <p className="text-sm text-default-500 leading-relaxed">
                  {t("platformInterest.description")}
                </p>
                <Button
                  className="w-full bg-black hover:bg-black/90 text-white font-medium"
                  onClick={() => window.open("/", "_blank")}
                >
                  {t("common:learnMore")}
                </Button>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
